import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Trophy, 
  Zap, 
  Calendar, 
  Clock, 
  Users, 
  Award, 
  Sparkles, 
  ArrowLeft, 
  Heart, 
  CheckCircle2, 
  ExternalLink, 
  DownloadCloud, 
  FileCode, 
  Layers, 
  Send, 
  X, 
  ShieldCheck, 
  Maximize2,
  Check,
  Columns,
  Flame,
  Share2
} from 'lucide-react';
import { 
  fetchCompetition, 
  voteCompetitionEntry, 
  submitCompetitionEntry,
  enrollCompetition,
  submitCompetitionAudition
} from '../api/client';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button';
import ShareModal from '../components/ShareModal';
import ComparisonModal from '../components/ComparisonModal';
import './CompetitionDetail.css';

// ── Live Countdown Hook ──
function useCountdown(targetDate) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, isEnded: false });

  useEffect(() => {
    if (!targetDate) return;

    function calculate() {
      const difference = new Date(targetDate).getTime() - new Date().getTime();
      if (difference <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, isEnded: true });
        return;
      }

      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
        isEnded: false,
      });
    }

    calculate();
    const timer = setInterval(calculate, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  return timeLeft;
}

export default function CompetitionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { setAuthOpen } = useOutletContext() || {};

  const [activeTab, setActiveTab] = useState('entries'); // 'entries' | 'brief' | 'leaderboard' | 'jury'
  const [activeMediaModal, setActiveMediaModal] = useState(null);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  // Selection for Side-by-Side Comparison
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [compareModalOpen, setCompareModalOpen] = useState(false);

  // Single User Vote state: store which entry user voted for in this competition
  const [userVotedEntryId, setUserVotedEntryId] = useState(null);

  // Submission form state
  const [submitForm, setSubmitForm] = useState({
    title: '',
    description: '',
    preview_url: '',
    figma_url: '',
    source_file_url: '',
    software_used: ['Figma'],
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['competition', id],
    queryFn: () => fetchCompetition(id),
  });

  // Mock Fallback Competition Data
  const defaultComp = {
    id: id || 1,
    title: "FinTech 2030: Next-Gen Banking OS Interface",
    description: "Design an ultra-clean, spatial finance dashboard that redefines how high-net-worth investors track global liquidity, crypto assets, and AI automated trading portfolios.",
    format_type: "tournament",
    status: "active_submission",
    category: "UI / UX Design",
    prize_pool: "$3,500 Cash Bounty",
    sponsor: "Linear Finance Lab",
    entry_count: 38,
    submission_deadline: new Date(Date.now() + 86400000 * 5).toISOString(),
    voting_deadline: new Date(Date.now() + 86400000 * 8).toISOString(),
    banner_url: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1200&q=80",
    deliverables: [
      "Figma Community Link with Auto-Layout Components & Design Tokens",
      "Interactive Prototype showcasing Transactions & Dashboard Drill-downs",
      "High-resolution 4K Cover Hero Mockup (16:9)",
      "Design Rationale Documentation & Typography Scale Spec"
    ],
    rubric: [
      { vector: "Visual Hierarchy & Spatial Balance", weight: "30%", desc: "Clear focal points, data density balance, and elegant card micro-surfaces." },
      { vector: "Typography & Contrast Ratio", weight: "30%", desc: "Strict type scale pairing, tabular numeral alignment, and AAA contrast compliance." },
      { vector: "Originality & Interaction Model", weight: "25%", desc: "Novel fintech interaction patterns and smooth micro-state flows." },
      { vector: "Component Hygiene & Tokens", weight: "15%", desc: "Organized Figma variants, layer naming, and scalable variables." }
    ],
    entries: [
      {
        id: 'e-1',
        title: "AuraOS: Spatial Wealth Engine",
        author_name: "Elena Rostova",
        author_avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&q=80",
        preview_url: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800&q=80",
        votes: 184,
        software_used: ["Figma", "Spline", "Photoshop"],
        figma_url: "https://figma.com",
        created_at: "2 hours ago",
        rank: 1
      },
      {
        id: 'e-2',
        title: "KRONOS Quantum Portfolio",
        author_name: "Marcus Vance",
        author_avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&q=80",
        preview_url: "https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?w=800&q=80",
        votes: 142,
        software_used: ["Figma", "After Effects"],
        figma_url: "https://figma.com",
        created_at: "5 hours ago",
        rank: 2
      },
      {
        id: 'e-3',
        title: "NeoBank Minimalist Dark Core",
        author_name: "Kwame Adu",
        author_avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&q=80",
        preview_url: "https://images.unsplash.com/photo-1600132806370-bf17e65e942f?w=800&q=80",
        votes: 119,
        software_used: ["Figma", "Illustrator"],
        figma_url: "https://figma.com",
        created_at: "1 day ago",
        rank: 3
      },
      {
        id: 'e-4',
        title: "Verve Liquidity Matrix",
        author_name: "Sarah Chen",
        author_avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&q=80",
        preview_url: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=800&q=80",
        votes: 97,
        software_used: ["Figma", "Protopie"],
        figma_url: "https://figma.com",
        created_at: "2 days ago",
        rank: 4
      }
    ]
  };

  const payload = data?.data || data;
  const comp = payload?.competition || defaultComp;
  const entries = (payload?.entries && payload.entries.length > 0) ? payload.entries : (comp.entries || defaultComp.entries);
  const matches = payload?.matches || [];
  const participantCount = payload?.participantCount ?? comp.entry_count ?? entries.length;
  const userContext = payload?.userContext || {};
  const countdown = useCountdown(comp.status === 'active_voting' ? comp.voting_deadline : comp.submission_deadline);

  // Sync user vote state from server
  useEffect(() => {
    if (userContext.votedEntryIds && userContext.votedEntryIds.length > 0) {
      setUserVotedEntryId(userContext.votedEntryIds[0]);
    }
  }, [userContext.votedEntryIds]);

  // Enrollment & Audition state
  const [enrolling, setEnrolling] = useState(false);
  const [auditionModalOpen, setAuditionModalOpen] = useState(false);
  const [auditionForm, setAuditionForm] = useState({
    audition_portfolio_url: '',
    audition_note: '',
  });

  const [votingId, setVotingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Vote for entry handler (API backed)
  const handleVote = async (entryId) => {
    if (!user) {
      if (setAuthOpen) setAuthOpen(true);
      return;
    }

    setVotingId(entryId);
    try {
      await voteCompetitionEntry(comp.id, { entry_id: entryId });
      setUserVotedEntryId(entryId);
      setToastMessage("✨ Your official vote has been recorded!");
      queryClient.invalidateQueries({ queryKey: ['competition', id] });
    } catch (err) {
      setToastMessage(err.message || "Failed to submit vote");
    } finally {
      setVotingId(null);
      setTimeout(() => setToastMessage(null), 3500);
    }
  };

  // Toggle comparison selection (allows comparing any 2 designs)
  const toggleCompare = (entry) => {
    if (selectedForCompare.some(e => e.id === entry.id)) {
      setSelectedForCompare(selectedForCompare.filter(e => e.id !== entry.id));
    } else {
      if (selectedForCompare.length < 2) {
        setSelectedForCompare([...selectedForCompare, entry]);
      } else {
        setSelectedForCompare([selectedForCompare[1], entry]);
      }
    }
  };

  const handleOpenSubmit = () => {
    if (!user) {
      if (setAuthOpen) setAuthOpen(true);
      return;
    }
    setSubmitModalOpen(true);
  };

  const handleSubmitEntry = async (e) => {
    e.preventDefault();
    if (!user) {
      if (setAuthOpen) setAuthOpen(true);
      return;
    }
    setSubmitting(true);
    try {
      await submitCompetitionEntry(comp.id, submitForm);
      setSubmitModalOpen(false);
      setToastMessage("🚀 Your design has been submitted to the challenge!");
      queryClient.invalidateQueries({ queryKey: ['competition', id] });
      setSubmitForm({
        title: '',
        description: '',
        preview_url: '',
        figma_url: '',
        source_file_url: '',
        software_used: ['Figma'],
      });
    } catch (err) {
      setToastMessage(err.message || "Failed to submit entry");
    } finally {
      setSubmitting(false);
      setTimeout(() => setToastMessage(null), 4000);
    }
  };

  const handleEnroll = async () => {
    if (!user) {
      if (setAuthOpen) setAuthOpen(true);
      return;
    }
    setEnrolling(true);
    try {
      const res = await enrollCompetition(comp.id);
      setToastMessage(res.message || "🎉 You are now enrolled in this challenge!");
      queryClient.invalidateQueries({ queryKey: ['competition', id] });
    } catch (err) {
      setToastMessage(err.message || "Failed to enroll");
    } finally {
      setEnrolling(false);
      setTimeout(() => setToastMessage(null), 3500);
    }
  };

  const handleAuditionSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      if (setAuthOpen) setAuthOpen(true);
      return;
    }
    setEnrolling(true);
    try {
      const res = await submitCompetitionAudition(comp.id, auditionForm);
      setAuditionModalOpen(false);
      setToastMessage(res.message || "🌟 Audition submitted! Academy Jury review is in progress.");
      queryClient.invalidateQueries({ queryKey: ['competition', id] });
    } catch (err) {
      setToastMessage(err.message || "Failed to submit audition");
    } finally {
      setEnrolling(false);
      setTimeout(() => setToastMessage(null), 4000);
    }
  };

  const toggleSoftware = (tool) => {
    setSubmitForm(prev => {
      const exists = prev.software_used.includes(tool);
      return {
        ...prev,
        software_used: exists ? prev.software_used.filter(t => t !== tool) : [...prev.software_used, tool]
      };
    });
  };

  // Prepare tournament matches
  const displayMatches = matches.length > 0 ? matches.map(m => {
    const entryA = entries.find(e => e.id === m.entry_a_id) || {
      id: m.entry_a_id,
      title: m.entry_a_title,
      preview_url: m.entry_a_preview,
      author_name: m.entry_a_author,
      votes: m.votes_a || 0
    };
    const entryB = entries.find(e => e.id === m.entry_b_id) || {
      id: m.entry_b_id,
      title: m.entry_b_title,
      preview_url: m.entry_b_preview,
      author_name: m.entry_b_author,
      votes: m.votes_b || 0
    };
    return {
      ...m,
      entryA,
      entryB,
      round_label: `Round ${m.round || 1} Elimination`
    };
  }) : (entries.length >= 2 ? [
    {
      id: 'match-sim-1',
      round_label: 'Championship Playoff · Duel 1',
      status: 'active',
      entryA: entries[0],
      entryB: entries[1]
    },
    ...(entries.length >= 4 ? [{
      id: 'match-sim-2',
      round_label: 'Championship Playoff · Duel 2',
      status: 'active',
      entryA: entries[2],
      entryB: entries[3]
    }] : [])
  ] : []);

  return (
    <div className="comp-detail-page page-container" style={{ paddingBottom: '140px', paddingTop: '16px' }}>
      
      {/* ── Breadcrumb & Action Row ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0 20px', flexWrap: 'wrap', gap: '12px' }}>
        <Link
          to="/competitions"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--text-secondary)',
            fontSize: '0.875rem',
            textDecoration: 'none',
            fontWeight: 600
          }}
        >
          <ArrowLeft size={16} /> Back to all challenges
        </Link>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShareModalOpen(true)}
            className="tactile-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '12px',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-primary)',
              fontSize: '0.8125rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            <Share2 size={14} /> Share Challenge
          </button>

          {userContext?.isEnrolled ? (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '9999px',
              background: userContext.enrollmentStatus === 'audition_pending' ? 'rgba(234, 179, 8, 0.15)' : 'rgba(16, 185, 129, 0.15)',
              border: `1px solid ${userContext.enrollmentStatus === 'audition_pending' ? '#EAB308' : '#10B981'}`,
              color: userContext.enrollmentStatus === 'audition_pending' ? '#FACC15' : '#10B981',
              fontSize: '0.8125rem',
              fontWeight: 700
            }}>
              {userContext.enrollmentStatus === 'audition_pending' ? (
                <>
                  <Clock size={14} /> Audition Under Review
                </>
              ) : (
                <>
                  <CheckCircle2 size={14} /> Registered Contender
                </>
              )}
            </div>
          ) : comp.entry_type === 'curated' || comp.entry_type === 'audition' ? (
            <Button variant="secondary" onClick={() => user ? setAuditionModalOpen(true) : (setAuthOpen && setAuthOpen(true))} disabled={enrolling}>
              <Sparkles size={14} /> Audition for Bracket
            </Button>
          ) : (
            <Button variant="secondary" onClick={handleEnroll} disabled={enrolling}>
              <Zap size={14} /> {enrolling ? 'Enrolling...' : 'Enroll in Challenge'}
            </Button>
          )}
          
          <Button variant="primary" onClick={handleOpenSubmit}>
            <Send size={15} /> {userContext?.hasSubmitted ? 'Update Entry' : 'Submit Your Entry'}
          </Button>
        </div>
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          background: 'var(--bg-surface-active)',
          border: '1px solid var(--primary)',
          color: '#FFFFFF',
          padding: '12px 20px',
          borderRadius: '14px',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          animation: 'fadeIn 0.2s ease'
        }}>
          <Sparkles size={16} color="var(--accent)" />
          <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{toastMessage}</span>
        </div>
      )}

      {/* ── Challenge Header Banner ── */}
      <div className="comp-detail-header interactive-card">
        <div className="comp-header-backdrop" style={{ backgroundImage: `url(${comp.banner_url})` }} />
        <div className="comp-header-overlay" />

        <div className="comp-header-content">
          <div className="comp-header-tags">
            <span className="comp-tag-pill tournament">
              <Trophy size={14} /> Championship Challenge
            </span>
            <span className="comp-live-pill">
              <span className="live-pulse-dot" />
              {comp.status === 'active_voting' ? 'COMMUNITY VOTING OPEN' : 'ACCEPTING SUBMISSIONS'}
            </span>
            {comp.sponsor && (
              <span className="comp-sponsor-pill">Sponsored by <strong>{comp.sponsor}</strong></span>
            )}
          </div>

          <h1 className="comp-header-title">{comp.title}</h1>
          <p className="comp-header-desc">{comp.description}</p>

          {/* Quick Metrics Bar */}
          <div className="comp-header-metrics">
            <div className="metric-box">
              <span className="metric-label">Prize Bounty</span>
              <strong className="metric-val prize">{comp.prize_pool}</strong>
            </div>

            <div className="metric-box">
              <span className="metric-label">Time Remaining</span>
              <div className="countdown-clock">
                <div className="clock-unit"><strong>{countdown.days}</strong><span>Days</span></div>
                <span className="clock-divider">:</span>
                <div className="clock-unit"><strong>{countdown.hours}</strong><span>Hours</span></div>
                <span className="clock-divider">:</span>
                <div className="clock-unit"><strong>{countdown.minutes}</strong><span>Min</span></div>
                <span className="clock-divider">:</span>
                <div className="clock-unit"><strong>{countdown.seconds}</strong><span>Sec</span></div>
              </div>
            </div>

            <div className="metric-box">
              <span className="metric-label">Total Submissions</span>
              <strong className="metric-val">{entries.length} Contenders</strong>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tabbed Content Navigation ── */}
      <div className="comp-detail-nav-tabs">
        {[
          { id: 'entries', label: `Submitted Entries (${entries.length})`, icon: Layers },
          { id: 'battles', label: `Head-to-Head Arena (${displayMatches.length})`, icon: Flame },
          { id: 'brief', label: 'Design Brief & Rubric', icon: FileCode },
          { id: 'leaderboard', label: 'Live Leaderboard', icon: Trophy },
          { id: 'jury', label: 'Jury & Evaluation Standards', icon: ShieldCheck }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`comp-tab-btn tactile-btn ${activeTab === tab.id ? 'active' : ''}`}
          >
            <tab.icon size={15} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* ── TAB 1: Submitted Entries Gallery ── */}
      {activeTab === 'entries' && (
        <div className="entries-gallery-section animate-fade-in">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 800 }}>Contender Submissions</h3>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Browse all contender entries, select any two to compare side-by-side, and vote for your top favorite.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => {
                  setSelectedForCompare([entries[0], entries[1]]);
                  setCompareModalOpen(true);
                }}
                className="tactile-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  borderRadius: '10px',
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-subtle)',
                  color: 'var(--text-primary)',
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                <Columns size={15} /> Compare Top 2
              </button>

              <Button variant="primary" onClick={handleOpenSubmit}>
                <Send size={14} /> Submit Entry
              </Button>
            </div>
          </div>

          <div className="entries-grid">
            {entries.map(entry => {
              const isVoted = userVotedEntryId === entry.id;
              const isSelected = selectedForCompare.some(e => e.id === entry.id);
              const totalVotes = entry.votes + (isVoted ? 1 : 0);

              return (
                <div key={entry.id} className="entry-card interactive-card" style={{ border: isVoted ? '2px solid var(--accent)' : isSelected ? '2px solid var(--primary)' : '1px solid var(--border-subtle)' }}>
                  
                  {/* Image Preview with Hover Action */}
                  <div className="entry-card-media" onClick={() => setActiveMediaModal(entry)}>
                    <img src={entry.preview_url} alt={entry.title} />
                    
                    {/* Compare Selection Checkbox Badge */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleCompare(entry); }}
                      className="tactile-btn"
                      style={{
                        position: 'absolute',
                        top: '12px',
                        left: '12px',
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: isSelected ? 'var(--primary)' : 'rgba(9, 9, 16, 0.85)',
                        backdropFilter: 'blur(8px)',
                        color: '#FFFFFF',
                        padding: '4px 10px',
                        borderRadius: '999px',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        cursor: 'pointer'
                      }}
                    >
                      <Columns size={12} />
                      {isSelected ? 'Selected' : 'Compare'}
                    </button>

                    <div className="entry-media-overlay">
                      <div className="entry-zoom-btn">
                        <Maximize2 size={16} /> Zoom Preview
                      </div>
                    </div>
                  </div>

                  {/* Entry Card Body */}
                  <div className="entry-card-body">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img src={entry.author_avatar} alt={entry.author_name} style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' }} />
                        <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-primary)' }}>{entry.author_name}</span>
                      </div>
                      <span style={{ fontSize: '0.6875rem', color: 'var(--text-tertiary)' }}>{entry.created_at}</span>
                    </div>

                    <h4 style={{ fontSize: '1rem', fontWeight: 800, marginBottom: '12px', color: 'var(--text-primary)' }}>{entry.title}</h4>

                    {/* Tools badges */}
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
                      {entry.software_used?.map(sw => (
                        <span key={sw} className="tool-chip">{sw}</span>
                      ))}
                    </div>

                    {/* Footer Vote Button & Figma Link */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '12px', borderTop: '1px solid var(--border-subtle)' }}>
                      <button
                        onClick={() => handleVote(entry.id)}
                        className="tactile-btn"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 16px',
                          borderRadius: '10px',
                          background: isVoted ? 'var(--accent)' : 'var(--bg-base)',
                          border: `1.5px solid ${isVoted ? 'var(--accent)' : 'var(--border-subtle)'}`,
                          color: isVoted ? '#000000' : 'var(--text-primary)',
                          fontSize: '0.8125rem',
                          fontWeight: 800,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        {isVoted ? <Check size={14} /> : <Heart size={14} />}
                        <span>{isVoted ? 'Voted Winner' : `Vote (${totalVotes})`}</span>
                      </button>

                      {entry.figma_url && (
                        <a
                          href={entry.figma_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px', textDecoration: 'none' }}
                        >
                          Figma File <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── TAB: Head-to-Head Arena & Battles ── */}
      {activeTab === 'battles' && (
        <div className="battles-section animate-fade-in">
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <Flame size={20} color="var(--primary)" />
              <h3 style={{ fontSize: '1.3rem', fontWeight: 800 }}>Championship Tournament Bracket & Duels</h3>
            </div>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Direct head-to-head battles. Inspect both contenders side-by-side using the interactive split-screen comparison and vote on the victor.
            </p>
          </div>

          {displayMatches.length === 0 ? (
            <div style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '20px',
              padding: '48px',
              textAlign: 'center',
              color: 'var(--text-secondary)'
            }}>
              <Trophy size={36} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
              <h4 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px' }}>Tournament Matchups Generating</h4>
              <p style={{ fontSize: '0.875rem', maxWidth: '440px', margin: '0 auto' }}>
                Once contender submissions reach the qualifying threshold, the Academy Jury algorithm automatically seeds tournament brackets.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {displayMatches.map((match, idx) => {
                const entryA = match.entryA;
                const entryB = match.entryB;
                const isVotedA = userVotedEntryId === entryA?.id;
                const isVotedB = userVotedEntryId === entryB?.id;

                return (
                  <div key={match.id || idx} className="interactive-card" style={{
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: '24px',
                    padding: '24px',
                    boxShadow: 'var(--shadow-md)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', flexWrap: 'wrap', gap: '8px' }}>
                      <span style={{
                        padding: '5px 14px',
                        borderRadius: '999px',
                        background: 'var(--primary-subtle)',
                        color: 'var(--primary-text)',
                        fontSize: '0.75rem',
                        fontWeight: 800,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        {match.round_label || `Match #${idx + 1}`}
                      </span>
                      <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span className="live-pulse-dot" />
                        {match.status === 'completed' ? 'Winner Decided' : 'Live Duel · Voting Open'}
                      </span>
                    </div>

                    {/* Duelists Row */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto 1fr',
                      alignItems: 'center',
                      gap: '16px'
                    }}>
                      {/* Contender A */}
                      <div style={{
                        background: 'var(--bg-base)',
                        borderRadius: '18px',
                        padding: '16px',
                        border: isVotedA ? '2px solid var(--accent)' : '1px solid var(--border-subtle)',
                        display: 'flex',
                        gap: '14px',
                        alignItems: 'center',
                        position: 'relative'
                      }}>
                        <img
                          src={entryA?.preview_url}
                          alt={entryA?.title}
                          style={{ width: '88px', height: '64px', borderRadius: '12px', objectFit: 'cover', flexShrink: 0, cursor: 'pointer' }}
                          onClick={() => setActiveMediaModal(entryA)}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.6875rem', textTransform: 'uppercase', fontWeight: 800, color: 'var(--primary)' }}>Contender A</div>
                          <div style={{ fontWeight: 800, fontSize: '0.9375rem', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {entryA?.title}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>By {entryA?.author_name}</div>
                        </div>
                        <button
                          onClick={() => handleVote(entryA.id)}
                          className="tactile-btn"
                          style={{
                            padding: '6px 12px',
                            borderRadius: '10px',
                            background: isVotedA ? 'var(--accent)' : 'transparent',
                            border: `1px solid ${isVotedA ? 'var(--accent)' : 'var(--border-subtle)'}`,
                            color: isVotedA ? '#000000' : 'var(--text-secondary)',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          {isVotedA ? 'Voted' : 'Vote'}
                        </button>
                      </div>

                      {/* VS Pill */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '46px',
                        height: '46px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #6366F1, #EC4899)',
                        color: '#FFFFFF',
                        fontWeight: 900,
                        fontSize: '0.8125rem',
                        boxShadow: '0 4px 16px rgba(99, 102, 241, 0.4)',
                        flexShrink: 0
                      }}>
                        VS
                      </div>

                      {/* Contender B */}
                      <div style={{
                        background: 'var(--bg-base)',
                        borderRadius: '18px',
                        padding: '16px',
                        border: isVotedB ? '2px solid var(--accent)' : '1px solid var(--border-subtle)',
                        display: 'flex',
                        gap: '14px',
                        alignItems: 'center',
                        position: 'relative'
                      }}>
                        <img
                          src={entryB?.preview_url}
                          alt={entryB?.title}
                          style={{ width: '88px', height: '64px', borderRadius: '12px', objectFit: 'cover', flexShrink: 0, cursor: 'pointer' }}
                          onClick={() => setActiveMediaModal(entryB)}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.6875rem', textTransform: 'uppercase', fontWeight: 800, color: '#EC4899' }}>Contender B</div>
                          <div style={{ fontWeight: 800, fontSize: '0.9375rem', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {entryB?.title}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>By {entryB?.author_name}</div>
                        </div>
                        <button
                          onClick={() => handleVote(entryB.id)}
                          className="tactile-btn"
                          style={{
                            padding: '6px 12px',
                            borderRadius: '10px',
                            background: isVotedB ? 'var(--accent)' : 'transparent',
                            border: `1px solid ${isVotedB ? 'var(--accent)' : 'var(--border-subtle)'}`,
                            color: isVotedB ? '#000000' : 'var(--text-secondary)',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          {isVotedB ? 'Voted' : 'Vote'}
                        </button>
                      </div>
                    </div>

                    {/* Duel Footer Action */}
                    <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                      <Button
                        variant="primary"
                        onClick={() => {
                          setSelectedForCompare([entryA, entryB]);
                          setCompareModalOpen(true);
                        }}
                      >
                        <Columns size={15} /> Enter Duel & Compare Side-by-Side
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── TAB 2: Design Brief & Rubric ── */}
      {activeTab === 'brief' && (
        <div className="brief-section animate-fade-in">
          <div className="brief-grid">
            
            {/* Left: Challenge Requirements */}
            <div className="brief-main-card glass-panel">
              <h3 style={{ fontSize: '1.35rem', fontWeight: 800, marginBottom: '12px' }}>Creative Prompt & Background</h3>
              <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '28px' }}>
                Next-generation financial systems require seamless cognitive clarity. Designers are tasked with creating a revolutionary spatial web interface for institutional investors. The interface must balance extreme data density (order books, live yield curves, gas fees, liquidity depth) with calming, minimalist aesthetics.
              </p>

              <h4 style={{ fontSize: '1.05rem', fontWeight: 800, marginBottom: '14px' }}>Required Submission Deliverables</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '32px' }}>
                {comp.deliverables?.map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', background: 'var(--bg-base)', padding: '14px', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
                    <CheckCircle2 size={18} color="var(--accent)" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '0.875rem', color: 'var(--text-primary)', lineHeight: 1.45 }}>{item}</span>
                  </div>
                ))}
              </div>

              <h4 style={{ fontSize: '1.05rem', fontWeight: 800, marginBottom: '14px' }}>Official Evaluation Rubric</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' }}>
                {comp.rubric?.map((rub, idx) => (
                  <div key={idx} style={{ background: 'var(--bg-base)', padding: '16px', borderRadius: '14px', border: '1px solid var(--border-subtle)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <strong style={{ fontSize: '0.875rem' }}>{rub.vector}</strong>
                      <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--primary-text)' }}>{rub.weight}</span>
                    </div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{rub.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Starter Kit Download Card */}
            <div className="brief-sidebar-card glass-panel">
              <h4 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: '8px' }}>Challenge Starter Kit</h4>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '20px' }}>
                Includes official design tokens, dummy crypto/liquidity dataset (.json), and Figma UI component starter frame.
              </p>

              <button
                type="button"
                onClick={() => { setToastMessage("Downloading Starter Pack (.zip)..."); setTimeout(() => setToastMessage(null), 3000); }}
                className="tactile-btn"
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '12px',
                  background: 'var(--primary)',
                  color: '#FFFFFF',
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '20px'
                }}
              >
                <DownloadCloud size={18} /> Download Starter Pack (18MB)
              </button>

              <div style={{ padding: '16px', background: 'var(--bg-base)', borderRadius: '12px', border: '1px solid var(--border-subtle)', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>Submission Deadline</div>
                <div>Sunday, Aug 24, 2026 · 11:59 PM UTC</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: Live Leaderboard ── */}
      {activeTab === 'leaderboard' && (
        <div className="leaderboard-section animate-fade-in">
          <div className="modern-table-container">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Design Contender</th>
                  <th>Submission Title</th>
                  <th>Software Stack</th>
                  <th>Community Votes</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, idx) => (
                  <tr key={entry.id}>
                    <td>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        fontWeight: 800,
                        fontSize: '0.8125rem',
                        background: idx === 0 ? '#FFD700' : idx === 1 ? '#C0C0C0' : idx === 2 ? '#CD7F32' : 'var(--bg-base)',
                        color: idx < 3 ? '#000000' : 'var(--text-secondary)'
                      }}>
                        {idx + 1}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img src={entry.author_avatar} alt={entry.author_name} style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }} />
                        <strong style={{ fontSize: '0.875rem' }}>{entry.author_name}</strong>
                      </div>
                    </td>
                    <td>
                      <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{entry.title}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {entry.software_used?.map(s => (
                          <span key={s} className="tool-chip">{s}</span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <strong style={{ color: 'var(--primary-text)' }}>
                        {entry.votes + (userVotedEntryId === entry.id ? 1 : 0)} votes
                      </strong>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        onClick={() => setActiveMediaModal(entry)}
                        className="tactile-btn"
                        style={{ padding: '6px 12px', borderRadius: '8px', background: 'var(--bg-base)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                      >
                        Inspect
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TAB 4: Jury & Standards ── */}
      {activeTab === 'jury' && (
        <div className="jury-section animate-fade-in">
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '24px', padding: '36px' }}>
            <h3 style={{ fontSize: '1.35rem', fontWeight: 800, marginBottom: '8px' }}>Expert Jury & Evaluation Panel</h3>
            <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)', marginBottom: '28px' }}>
              Our guest judges are executive design leaders from the world's most demanding design studios.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
              {[
                { name: "Julian Thorne", role: "Principal Design Director, Stripe", avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&q=80" },
                { name: "Aria Sterling", role: "Head of 3D & Brand, Vercel", avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&q=80" },
                { name: "Kofi Mensah", role: "Design Partner, Studio Accrate", avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&q=80" }
              ].map((judge, idx) => (
                <div key={idx} style={{ background: 'var(--bg-base)', padding: '20px', borderRadius: '16px', border: '1px solid var(--border-subtle)', display: 'flex', gap: '14px', alignItems: 'center' }}>
                  <img src={judge.avatar} alt={judge.name} style={{ width: '52px', height: '52px', borderRadius: '14px', objectFit: 'cover' }} />
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>{judge.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{judge.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Floating Compare Bottom Tray ── */}
      {selectedForCompare.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-medium)',
          borderRadius: '9999px',
          padding: '8px 16px 8px 24px',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.65)',
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          animation: 'fadeIn 0.2s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-primary)' }}>
              {selectedForCompare.length} {selectedForCompare.length === 1 ? 'design' : 'designs'} selected for Comparison
            </span>
            <div style={{ display: 'flex', gap: '4px' }}>
              {selectedForCompare.map(e => (
                <img key={e.id} src={e.preview_url} alt={e.title} style={{ width: '28px', height: '28px', borderRadius: '6px', objectFit: 'cover', border: '1px solid var(--primary)' }} />
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {selectedForCompare.length === 2 ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setCompareModalOpen(true)}
              >
                <Columns size={14} /> Compare Side-by-Side
              </Button>
            ) : (
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Select 1 more to compare</span>
            )}

            <button
              onClick={() => setSelectedForCompare([])}
              className="tactile-btn"
              style={{ padding: '6px', background: 'transparent', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer' }}
              title="Clear selection"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ── Side-by-Side Dual Comparison Modal ── */}
      {compareModalOpen && selectedForCompare.length >= 2 && (
        <ComparisonModal
          isOpen={compareModalOpen}
          onClose={() => setCompareModalOpen(false)}
          entryA={selectedForCompare[0]}
          entryB={selectedForCompare[1]}
          onVote={(id) => handleVote(id)}
        />
      )}

      {/* ── Fullscreen Lightbox Zoom Modal ── */}
      {activeMediaModal && (
        <div className="comp-lightbox-modal" onClick={() => setActiveMediaModal(null)}>
          <div className="lightbox-container" onClick={e => e.stopPropagation()}>
            <div className="lightbox-header">
              <div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 800 }}>{activeMediaModal.title}</h3>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>By {activeMediaModal.author_name}</span>
              </div>
              <button onClick={() => setActiveMediaModal(null)} className="lightbox-close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="lightbox-body">
              <img src={activeMediaModal.preview_url} alt={activeMediaModal.title} />
            </div>
          </div>
        </div>
      )}

      {/* ── Submit Entry Modal ── */}
      {submitModalOpen && (
        <div className="comp-lightbox-modal" onClick={() => setSubmitModalOpen(false)}>
          <div className="submit-modal-box" onClick={e => e.stopPropagation()}>
            <div className="lightbox-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ padding: '8px', borderRadius: '10px', background: 'var(--primary-subtle)', color: 'var(--primary)' }}>
                  <Send size={18} />
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800 }}>Submit Your Challenge Entry</h3>
              </div>
              <button onClick={() => setSubmitModalOpen(false)} className="lightbox-close-btn">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmitEntry} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div className="form-group">
                <label>Entry Title <span style={{ color: 'var(--primary)' }}>*</span></label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. AuraOS Spatial Finance Engine"
                  value={submitForm.title}
                  onChange={e => setSubmitForm({ ...submitForm, title: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Primary Preview Image URL <span style={{ color: 'var(--primary)' }}>*</span></label>
                <input
                  type="url"
                  className="form-input"
                  placeholder="https://images.unsplash.com/photo-..."
                  value={submitForm.preview_url}
                  onChange={e => setSubmitForm({ ...submitForm, preview_url: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Live Figma Community / Prototype Link</label>
                <input
                  type="url"
                  className="form-input"
                  placeholder="https://figma.com/file/..."
                  value={submitForm.figma_url}
                  onChange={e => setSubmitForm({ ...submitForm, figma_url: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Design Rationale & Concept Statement</label>
                <textarea
                  className="form-input"
                  placeholder="Explain your visual hierarchy choices, typography pairing, and spatial UI considerations..."
                  style={{ minHeight: '100px', resize: 'vertical' }}
                  value={submitForm.description}
                  onChange={e => setSubmitForm({ ...submitForm, description: e.target.value })}
                />
              </div>

              <div>
                <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
                  Software Stack Used
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {['Figma', 'Photoshop', 'Illustrator', 'Blender', 'After Effects', 'Spline', 'Protopie'].map(tool => {
                    const isSelected = submitForm.software_used.includes(tool);
                    return (
                      <button
                        key={tool}
                        type="button"
                        onClick={() => toggleSoftware(tool)}
                        className="tactile-btn"
                        style={{
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          background: isSelected ? 'var(--primary)' : 'var(--bg-base)',
                          color: isSelected ? '#FFFFFF' : 'var(--text-secondary)',
                          border: `1px solid ${isSelected ? 'var(--primary)' : 'var(--border-subtle)'}`,
                          cursor: 'pointer'
                        }}
                      >
                        {tool}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px', paddingTop: '16px', borderTop: '1px solid var(--border-subtle)' }}>
                <Button variant="ghost" type="button" onClick={() => setSubmitModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit">
                  🚀 Launch Entry to Challenge
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Audition Submission Modal ── */}
      {auditionModalOpen && (
        <div className="comp-lightbox-modal" onClick={() => setAuditionModalOpen(false)}>
          <div className="submit-modal-box" onClick={e => e.stopPropagation()}>
            <div className="lightbox-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ padding: '8px', borderRadius: '10px', background: 'rgba(236, 72, 153, 0.15)', color: '#EC4899' }}>
                  <Sparkles size={18} />
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800 }}>Submit Qualification Audition</h3>
              </div>
              <button onClick={() => setAuditionModalOpen(false)} className="lightbox-close-btn">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleAuditionSubmit} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                This is a curated championship challenge. Provide your design portfolio or live showcase link so our Academy Jury can evaluate your qualification for the tournament bracket.
              </p>

              <div className="form-group">
                <label>Portfolio / Case Study URL <span style={{ color: 'var(--primary)' }}>*</span></label>
                <input
                  type="url"
                  className="form-input"
                  placeholder="https://dribbble.com/yourname or Figma showcase..."
                  value={auditionForm.audition_portfolio_url}
                  onChange={e => setAuditionForm({ ...auditionForm, audition_portfolio_url: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Audition Note & Design Specialization</label>
                <textarea
                  className="form-input"
                  placeholder="Briefly describe your design background, tools mastery, and vision for this challenge..."
                  style={{ minHeight: '100px', resize: 'vertical' }}
                  value={auditionForm.audition_note}
                  onChange={e => setAuditionForm({ ...auditionForm, audition_note: e.target.value })}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px', paddingTop: '16px', borderTop: '1px solid var(--border-subtle)' }}>
                <Button variant="ghost" type="button" onClick={() => setAuditionModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={enrolling}>
                  {enrolling ? 'Submitting...' : '✨ Submit for Jury Review'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        title={`Share ${comp.title}`}
      />
    </div>
  );
}
